import React, { Component } from 'react';
import moment from 'moment';
import DateInput from '../../../sections/proposal-request/components/DateInput';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const num = (v) => Number(v) || 0;
const sumColumn = (rows, key) => rows.reduce((sum, row) => sum + num(row[key]), 0);

class PreferredDatesStep extends Component {
	getProgramDaysFromRange = (arrivalDate, departureDate) => {
		if (!arrivalDate || !departureDate) return [];

		const start = moment(arrivalDate, 'YYYY-MM-DD', true);
		const end = moment(departureDate, 'YYYY-MM-DD', true);
		if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day')) return [];

		const programDays = [];
		const seenDays = new Set();
		const current = start.clone();

		while (current.isSameOrBefore(end, 'day')) {
			const day = current.format('ddd');
			if (!seenDays.has(day)) {
				seenDays.add(day);
				programDays.push(day);
			}
			current.add(1, 'days');
		}

		return programDays;
	};

	syncProgramDaysToDates = (arrivalDate, departureDate) => {
		const { onChange } = this.props;
		onChange('programDays', this.getProgramDaysFromRange(arrivalDate, departureDate));
	};

	syncRoomBlockToDates = (arrivalDate, departureDate) => {
		const { data, onChange } = this.props;
		if (!arrivalDate || !departureDate) return;

		const existingRoomBlock = data.roomBlock || [];
		const existingByDate = {};
		existingRoomBlock.forEach(row => {
			if (row.date) existingByDate[row.date] = row;
		});

		const dates = [];
		let current = moment(arrivalDate);
		const end = moment(departureDate);
		while (current.isSameOrBefore(end, 'day')) {
			dates.push(current.format('YYYY-MM-DD'));
			current.add(1, 'days');
		}

		const newRoomBlock = dates.map(date =>
			existingByDate[date]
				? existingByDate[date]
				: { date, king: 0, double: 0, suite: 0, staff: 0, total: 0 }
		);

		onChange('roomBlock', newRoomBlock);
	};

	addAlternateDate = () => {
		const { data, onChange } = this.props;
		const alternativeDates = [...(data.alternativeDates || [])];
		alternativeDates.push({ arrivalDate: '', departureDate: '' });
		onChange('alternativeDates', alternativeDates);
	};

	removeAlternateDate = (index) => {
		const { data, onChange } = this.props;
		const alternativeDates = [...(data.alternativeDates || [])];
		alternativeDates.splice(index, 1);
		onChange('alternativeDates', alternativeDates);
	};

	updateAlternateDate = (index, field, value) => {
		const { data, onChange } = this.props;
		const alternativeDates = [...(data.alternativeDates || [])];
		alternativeDates[index] = { ...alternativeDates[index], [field]: value };
		onChange('alternativeDates', alternativeDates);
	};

	updateAlternateDateFields = (index, fields) => {
		const { data, onChange } = this.props;
		const alternativeDates = [...(data.alternativeDates || [])];
		alternativeDates[index] = { ...alternativeDates[index], ...fields };
		onChange('alternativeDates', alternativeDates);
	};

	addRoomBlockDay = () => {
		const { data, onChange } = this.props;
		const roomBlock = [...(data.roomBlock || [])];

		// Find the last row that has a valid date and default the new row to the next day
		let nextDate = '';
		for (let index = roomBlock.length - 1; index >= 0; index -= 1) {
			const currentDate = roomBlock[index]?.date;
			if (currentDate && moment(currentDate, 'YYYY-MM-DD', true).isValid()) {
				nextDate = moment(currentDate).add(1, 'days').format('YYYY-MM-DD');
				break;
			}
		}

		roomBlock.push({ date: nextDate, king: 0, double: 0, suite: 0, staff: 0, total: 0 });
		onChange('roomBlock', roomBlock);
	};

	removeRoomBlockDay = (index) => {
		const { data, onChange } = this.props;
		const roomBlock = [...(data.roomBlock || [])];
		roomBlock.splice(index, 1);
		onChange('roomBlock', roomBlock);
	};

	duplicateRoomBlockDay = (index) => {
		const { data, onChange } = this.props;
		const roomBlock = [...(data.roomBlock || [])];
		roomBlock.splice(index + 1, 0, { ...roomBlock[index] });
		onChange('roomBlock', roomBlock);
	};

	updateRoomBlock = (index, field, value) => {
		const { data, onChange } = this.props;
		const roomBlock = [...(data.roomBlock || [])];
		const parsedValue = field !== 'date' ? num(value) : value;
		roomBlock[index] = { ...roomBlock[index], [field]: parsedValue };

		if (field !== 'date' && field !== 'total') {
			const row = roomBlock[index];
			roomBlock[index].total = num(row.king) + num(row.double) + num(row.suite) + num(row.staff);
		}
		onChange('roomBlock', roomBlock);
	};

	toggleProgramDay = (day) => {
		const { data, onChange } = this.props;
		const programDays = [...(data.programDays || [])];
		const idx = programDays.indexOf(day);
		if (idx >= 0) {
			programDays.splice(idx, 1);
		} else {
			programDays.push(day);
		}
		onChange('programDays', programDays);
	};

	toggleAlternativeArrivalDay = (day) => {
		const { data, onChange } = this.props;
		const selectedDay = (data.altArrivalDays || [])[0];
		onChange('altArrivalDays', selectedDay === day ? [] : [day]);
	};

	render() {
		const { data, onChange } = this.props;
		const alternativeDates = data.alternativeDates || [];
		const roomBlock = data.roomBlock || [];
		const today = moment().format('YYYY-MM-DD');
		const programDays = this.getProgramDaysFromRange(data.preferredArrivalDate, data.preferredDepartureDate);
		const altArrivalDays = data.altArrivalDays || [];
		const selectedAltArrivalDay = altArrivalDays[0];
		const primaryArrivalDay = programDays[0];

		return (
			<div className="rfp-step-content">
				<h1 className="rfp-step-title">Preferred Dates</h1>

				{/* Primary Dates */}
				<div className="rfp-two-col-dates">
					<div className="rfp-field-group">
						<label className="rfp-field-label">Preferred Arrival Date</label>
						<div className="rfp-date-input-wrap">
							<DateInput
								className="rfp-date-input"
								value={data.preferredArrivalDate || ''}
								min={today}
								onChange={(newArrival) => {
									const newDeparture = newArrival
										? moment(newArrival).add(1, 'days').format('YYYY-MM-DD')
										: '';
									onChange('preferredArrivalDate', newArrival || '');
									onChange('preferredDepartureDate', newDeparture);
									this.syncProgramDaysToDates(newArrival, newDeparture);
									this.syncRoomBlockToDates(newArrival, newDeparture);
								}}
							/>
							<i className="las la-calendar rfp-date-icon" />
						</div>
						<div className="rfp-input-underline" />
					</div>
					<div className="rfp-field-group">
						<label className="rfp-field-label">Preferred Departure Date</label>
						<div className="rfp-date-input-wrap">
							<DateInput
								className="rfp-date-input"
								value={data.preferredDepartureDate || ''}
								min={data.preferredArrivalDate || today}
								onChange={(newDeparture) => {
									onChange('preferredDepartureDate', newDeparture || '');
									this.syncProgramDaysToDates(data.preferredArrivalDate, newDeparture);
									this.syncRoomBlockToDates(data.preferredArrivalDate, newDeparture);
								}}
							/>
							<i className="las la-calendar rfp-date-icon" />
						</div>
						<div className="rfp-input-underline" />
					</div>
				</div>

				{/* Alternative Dates */}
				{alternativeDates.map((altDate, index) => (
					<div key={index} className="rfp-two-col-dates rfp-alt-date-row">
						<div className="rfp-field-group">
							<div className="rfp-field-label-row">
								<label className="rfp-field-label">Alternative Arrival Day</label>
								<button
									className="rfp-remove-date-btn"
									onClick={() => this.removeAlternateDate(index)}
								>
									<i className="las la-times" />
								</button>
							</div>
							<div className="rfp-date-input-wrap">
								<DateInput
									min={today}
									className="rfp-date-input"
									value={altDate.arrivalDate || ''}
									onChange={(val) => {
										const newDeparture = val
											? moment(val).add(1, 'days').format('YYYY-MM-DD')
											: '';
									this.updateAlternateDateFields(index, { arrivalDate: val || '', departureDate: newDeparture });
									}}
								/>
								<i className="las la-calendar rfp-date-icon" />
							</div>
							<div className="rfp-input-underline" />
						</div>
						<div className="rfp-field-group">
							<label className="rfp-field-label">Alternative Departure Day</label>
							<div className="rfp-date-input-wrap">
								<DateInput
									className="rfp-date-input"
									value={altDate.departureDate || ''}
									min={data.preferredArrivalDate || today}
									onChange={(val) => this.updateAlternateDate(index, 'departureDate', val || '')}
								/>
								<i className="las la-calendar rfp-date-icon" />
							</div>
							<div className="rfp-input-underline" />
						</div>
					</div>
				))}

				<button className="rfp-add-item-btn" onClick={this.addAlternateDate}>
					<i className="las la-plus-circle" />
					<span>Add additional date options</span>
				</button>

				{/* Room Block */}
				<h1 className="rfp-step-title rfp-section-title">Room Block</h1>

				<div className="rfp-room-block-table">
					<div className="rfp-room-block-header">
						<div className="rfp-rb-col rfp-rb-date">Date</div>
						<div className="rfp-rb-col rfp-rb-num">King</div>
						<div className="rfp-rb-col rfp-rb-num">Double</div>
						<div className="rfp-rb-col rfp-rb-num">Suite</div>
						<div className="rfp-rb-col rfp-rb-num">Staff</div>
						<div className="rfp-rb-col rfp-rb-total">Total</div>
						<div className="rfp-rb-col rfp-rb-actions" />
					</div>

					{roomBlock.map((row, index) => (
						<div key={index} className="rfp-room-block-row">
							<div className="rfp-rb-col rfp-rb-date">
								<div className="rfp-date-input-wrap">
									<DateInput
										min={today}
										className="rfp-date-input rfp-rb-date-input"
										value={row.date || ''}
										onChange={(val) => this.updateRoomBlock(index, 'date', val || '')}
									/>
									<i className="las la-calendar rfp-date-icon" />
								</div>
							</div>
							<div className="rfp-rb-col rfp-rb-num">
								<input
									className="rfp-rb-num-input"
									type="number"
									min="0"
									value={row.king || 0}
									onChange={(e) => this.updateRoomBlock(index, 'king', e.target.value)}
								/>
							</div>
							<div className="rfp-rb-col rfp-rb-num">
								<input
									className="rfp-rb-num-input"
									type="number"
									min="0"
									value={row.double || 0}
									onChange={(e) => this.updateRoomBlock(index, 'double', e.target.value)}
								/>
							</div>
							<div className="rfp-rb-col rfp-rb-num">
								<input
									className="rfp-rb-num-input"
									type="number"
									min="0"
									value={row.suite || 0}
									onChange={(e) => this.updateRoomBlock(index, 'suite', e.target.value)}
								/>
							</div>
							<div className="rfp-rb-col rfp-rb-num">
								<input
									className="rfp-rb-num-input"
									type="number"
									min="0"
									value={row.staff || 0}
									onChange={(e) => this.updateRoomBlock(index, 'staff', e.target.value)}
								/>
							</div>
							<div className="rfp-rb-col rfp-rb-total">
								<div className={`rfp-rb-total-badge ${index === roomBlock.findIndex(r => r.total === Math.max(...roomBlock.map(r => r.total || 0))) ? 'highlight' : ''}`}>
									{row.total || 0}
								</div>
							</div>
							<div className="rfp-rb-col rfp-rb-actions">
								<button
									className="rfp-rb-action-btn"
									onClick={() => this.duplicateRoomBlockDay(index)}
									title="Duplicate"
								>
									<i className="las la-copy" />
								</button>
								<button
									className="rfp-rb-action-btn"
									onClick={() => this.removeRoomBlockDay(index)}
									title="Remove"
								>
									<i className="las la-times" />
								</button>
							</div>
						</div>
					))}

					{/* Footer totals */}
					{roomBlock.length > 0 && (() => {
						const totKing = sumColumn(roomBlock, 'king');
						const totDouble = sumColumn(roomBlock, 'double');
						const totSuite = sumColumn(roomBlock, 'suite');
						const totStaff = sumColumn(roomBlock, 'staff');
						const totAll = totKing + totDouble + totSuite + totStaff;
						return (
							<div
								className="rfp-room-block-row"
								style={{ borderTop: '2px solid #e5e7eb', background: '#f9fafb', fontWeight: 600 }}
							>
								<div className="rfp-rb-col rfp-rb-date" style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
									Totals
								</div>
								{[totKing, totDouble, totSuite, totStaff].map((val, i) => (
									<div key={i} className="rfp-rb-col rfp-rb-num" style={{ fontSize: 14 }}>{val}</div>
								))}
								<div className="rfp-rb-col rfp-rb-total">
									<div className="rfp-rb-total-badge" style={{ background: '#1a1a1a', color: '#fff' }}>{totAll}</div>
								</div>
								<div className="rfp-rb-col rfp-rb-actions" />
							</div>
						);
					})()}
				</div>

				<button className="rfp-add-item-btn" onClick={this.addRoomBlockDay}>
					<i className="las la-plus-circle" />
					<span>Add additional day</span>
				</button>

				{/* Preferred Program Pattern */}
				<h1 className="rfp-step-title rfp-section-title">Preferred Program Pattern</h1>

				{programDays.length > 0 && (
					<p className="rfp-program-desc">
						You have a {programDays.length} day program, with a {programDays[0]} arrival day.
					</p>
				)}

				<div className="rfp-day-selector">
					{DAYS_OF_WEEK.map((day) => (
						<button
							key={day}
							className={`rfp-day-btn ${programDays.includes(day) ? 'active' : ''} disabled-day`}
							disabled
						>
							{day}
						</button>
					))}
				</div>

				<div className="rfp-alt-arrival-toggle">
					<div
						className={`rfp-toggle-switch ${data.openToAltArrival ? 'active' : ''}`}
						onClick={() => onChange('openToAltArrival', !data.openToAltArrival)}
					>
						<div className="rfp-toggle-thumb" />
					</div>
					<span className="rfp-toggle-label">Are you open to an alternative arrival date?</span>
				</div>

				{data.openToAltArrival && (
					<div>
						<label className="rfp-field-label rfp-alt-days-label">Alternative Arrival Days</label>
						<div className="rfp-day-selector">
							{DAYS_OF_WEEK.map((day) => (
								<button
									key={day}
									className={`rfp-day-btn ${selectedAltArrivalDay === day ? 'active' : ''} ${primaryArrivalDay === day ? 'disabled-day' : ''}`}
									onClick={() => primaryArrivalDay !== day && this.toggleAlternativeArrivalDay(day)}
								>
									{day}
								</button>
							))}
						</div>
					</div>
				)}

				<div className="rfp-field-group rfp-notes-group">
					<label className="rfp-field-label">Date And Pattern Notes</label>
					<textarea
						className="rfp-textarea"
						value={data.datePatternNotes || ''}
						onChange={(e) => onChange('datePatternNotes', e.target.value)}
						placeholder="Please add any additional notes regarding your dates and arrival/departure pattern preferences."
						rows={4}
					/>
				</div>
			</div>
		);
	}
}

export default PreferredDatesStep;
